import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip as RechartsTooltip, Legend } from 'recharts';
import { motion, AnimatePresence } from 'framer-motion';

interface EMICalculatorProps {
    initialAmount?: number;
}

const COLORS = ['#001b4a', '#f1ae24']; // Navy and Gold

const EMICalculator: React.FC<EMICalculatorProps> = ({ initialAmount = 5000000 }) => {
    const [amount, setAmount] = useState<number>(initialAmount);
    const [rate, setRate] = useState<number>(8.5);
    const [tenure, setTenure] = useState<number>(20);

    // Dynamic Calculations
    const results = useMemo(() => {
        const p = amount;
        const r = rate / 12 / 100;
        const n = tenure * 12;

        if (r === 0) {
            return {
                emi: p / n,
                totalAmount: p,
                totalInterest: 0,
                principalPercentage: 100,
                interestPercentage: 0
            };
        }

        const emiValue = (p * r * Math.pow(1 + r, n)) / (Math.pow(1 + r, n) - 1);
        const totalAmt = emiValue * n;
        const totalInt = totalAmt - p;

        return {
            emi: emiValue,
            totalAmount: totalAmt,
            totalInterest: totalInt,
            principalPercentage: (p / totalAmt) * 100,
            interestPercentage: (totalInt / totalAmt) * 100
        };
    }, [amount, rate, tenure]);

    // Amortization Schedule (Yearly)
    const amortizationSchedule = useMemo(() => {
        const schedule = [];
        let remainingBalance = amount;
        const monthlyRate = rate / 12 / 100;
        const emi = results.emi;

        for (let year = 1; year <= tenure; year++) {
            let yearlyInterest = 0;
            let yearlyPrincipal = 0;

            for (let month = 1; month <= 12; month++) {
                const interestForMonth = remainingBalance * monthlyRate;
                const principalForMonth = emi - interestForMonth;
                yearlyInterest += interestForMonth;
                yearlyPrincipal += principalForMonth;
                remainingBalance -= principalForMonth;
            }

            schedule.push({
                year,
                openingBalance: remainingBalance + yearlyPrincipal,
                principalPaid: yearlyPrincipal,
                interestPaid: yearlyInterest,
                closingBalance: Math.max(0, remainingBalance)
            });
        }
        return schedule;
    }, [amount, rate, tenure, results.emi]);

    const formatCurrency = (val: number) => {
        return new Intl.NumberFormat('en-IN', {
            style: 'currency',
            currency: 'INR',
            maximumFractionDigits: 0,
        }).format(val);
    };

    const chartData = [
        { name: 'Principal Amount', value: amount },
        { name: 'Interest Amount', value: results.totalInterest },
    ];

    return (
        <div className="space-y-8 animate-in fade-in duration-700">
            {/* Main Calculator Card */}
            <div className="bg-white rounded-xl border border-slate-200 purple-card-shadow overflow-hidden relative">
                <div className="absolute top-0 left-0 w-full h-1.5 bg-gradient-to-r from-[#001b4a] via-[#f1ae24] to-[#001b4a]"></div>
                
                <div className="grid grid-cols-1 lg:grid-cols-12">
                    {/* Left Side: Inputs */}
                    <div className="lg:col-span-7 p-6 md:p-10 border-r border-slate-100">
                        <div className="flex items-center gap-3 mb-10">
                            <div className="w-10 h-10 rounded-xl bg-slate-100 flex items-center justify-center text-slate-900 shadow-sm border border-slate-200">
                                <i className="fa-solid fa-calculator text-base"></i>
                            </div>
                            <div>
                                <h3 className="text-lg font-bold text-slate-900 leading-none">Loan Inputs</h3>
                                <p className="text-xs font-medium text-slate-400 mt-1">Configure your loan parameters</p>
                            </div>
                        </div>

                        <div className="space-y-4">
                            {/* Amount Slider */}
                            <div className="space-y-2">
                                <div className="flex justify-between items-end">
                                    <label className="text-sm font-semibold text-slate-700">Loan Amount</label>
                                    <div className="relative group">
                                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 font-medium text-sm">₹</span>
                                        <input 
                                            type="number" 
                                            value={amount} 
                                            onChange={(e) => setAmount(Number(e.target.value))}
                                            className="bg-slate-50 border border-slate-200 rounded-xl pl-7 pr-4 py-2 text-right font-bold text-slate-900 focus:ring-2 focus:ring-[#f1ae24] focus:border-[#f1ae24] outline-none transition-all w-40 text-sm"
                                        />
                                    </div>
                                </div>
                                <input 
                                    type="range" 
                                    min="100000" 
                                    max="200000000" 
                                    step="100000" 
                                    value={amount} 
                                    onChange={(e) => setAmount(Number(e.target.value))}
                                    className="w-full h-2 bg-slate-100 rounded-lg appearance-none cursor-pointer accent-[#001b4a]"
                                />
                                <div className="flex justify-between text-[11px] font-medium text-slate-400">
                                    <span>₹ 1L</span>
                                    <span>₹ 20Cr</span>
                                </div>
                            </div>

                            {/* Rate Slider */}
                            <div className="space-y-2">
                                <div className="flex justify-between items-end">
                                    <label className="text-sm font-semibold text-slate-700">Interest Rate (%)</label>
                                    <div className="relative group">
                                        <input 
                                            type="number" 
                                            value={rate} 
                                            step="0.1"
                                            onChange={(e) => setRate(Number(e.target.value))}
                                            className="bg-slate-50 border border-slate-200 rounded-xl px-4 py-2 text-right font-bold text-slate-900 focus:ring-2 focus:ring-[#f1ae24] focus:border-[#f1ae24] outline-none transition-all w-24 text-sm"
                                        />
                                        <span className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 font-medium text-sm">%</span>
                                    </div>
                                </div>
                                <input 
                                    type="range" 
                                    min="1" 
                                    max="20" 
                                    step="0.1" 
                                    value={rate} 
                                    onChange={(e) => setRate(Number(e.target.value))}
                                    className="w-full h-2 bg-slate-100 rounded-lg appearance-none cursor-pointer accent-[#001b4a]"
                                />
                                <div className="flex justify-between text-[11px] font-medium text-slate-400">
                                    <span>1%</span>
                                    <span>20%</span>
                                </div>
                            </div>

                            {/* Tenure Slider */}
                            <div className="space-y-2">
                                <div className="flex justify-between items-end">
                                    <label className="text-sm font-semibold text-slate-700">Loan Tenure (Years)</label>
                                    <div className="relative group">
                                        <input 
                                            type="number" 
                                            value={tenure} 
                                            onChange={(e) => setTenure(Number(e.target.value))}
                                            className="bg-slate-50 border border-slate-200 rounded-xl px-4 py-2 text-right font-bold text-slate-900 focus:ring-2 focus:ring-[#f1ae24] focus:border-[#f1ae24] outline-none transition-all w-24 text-sm"
                                        />
                                        <span className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 font-medium text-[11px]">Yrs</span>
                                    </div>
                                </div>
                                <input 
                                    type="range" 
                                    min="1" 
                                    max="30" 
                                    step="1" 
                                    value={tenure} 
                                    onChange={(e) => setTenure(Number(e.target.value))}
                                    className="w-full h-2 bg-slate-100 rounded-lg appearance-none cursor-pointer accent-[#001b4a]"
                                />
                                <div className="flex justify-between text-[11px] font-medium text-slate-400">
                                    <span>1 Year</span>
                                    <span>30 Years</span>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Right Side: Insights & Summary */}
                    <div className="lg:col-span-5 bg-slate-50/50 p-6 md:p-8 flex flex-col gap-4 relative overflow-hidden">
                        <div className="absolute top-0 right-0 w-64 h-64 bg-slate-200/50 rounded-full blur-3xl -mr-32 -mt-32 pointer-events-none"></div>
                        
                        <div className="relative z-10 w-full">
                            <div className="bg-white p-6 rounded-2xl text-slate-900 shadow-sm mb-4 border border-slate-200 group transition-all duration-500 w-full relative overflow-hidden">
                                <p className="text-slate-500 font-semibold text-xs mb-2 text-center">Monthly EMI</p>
                                <h2 className="text-4xl font-bold text-[#001b4a] text-center mb-2 leading-none">
                                    {formatCurrency(results.emi)}
                                </h2>
                                <div className="w-full h-px bg-slate-100 my-4"></div>
                                <div className="grid grid-cols-2 gap-4">
                                    <div className="text-center">
                                        <p className="text-slate-400 text-[11px] font-medium mb-1 leading-none">Total Interest</p>
                                        <p className="font-bold text-slate-800 text-base">{formatCurrency(results.totalInterest)}</p>
                                    </div>
                                    <div className="text-center border-l border-slate-100">
                                        <p className="text-slate-400 text-[11px] font-medium mb-1 leading-none">Total Payment</p>
                                        <p className="font-bold text-[#f1ae24] text-base">{formatCurrency(results.totalAmount)}</p>
                                    </div>
                                </div>
                            </div>
                            <Link 
                                to="/login" 
                                className="block w-full post-property-btn-effect text-white p-4 rounded-2xl text-center shadow-lg transition-all active:scale-95 group relative overflow-hidden mb-6"
                            >
                                <div className="shimmer-effect-layer"></div>
                                <div className="flex items-center justify-center gap-2 relative z-10">
                                    <i className="fa-solid fa-plus-circle text-lg"></i>
                                    <span className="font-bold text-xs">Post Your Property Free</span>
                                </div>
                                <p className="text-[10px] font-medium opacity-80 mt-1 relative z-10">Join 10,000+ happy sellers today!</p>
                            </Link>

                            {/* Breakdown Chart */}
                            <div className="w-full flex flex-col items-center py-4">
                                <div className="h-[180px] md:h-[220px] w-full">
                                    <ResponsiveContainer width="100%" height="100%">
                                        <PieChart>
                                            <Pie
                                                data={chartData}
                                                cx="50%"
                                                cy="50%"
                                                innerRadius={50}
                                                outerRadius={80}
                                                paddingAngle={8}
                                                dataKey="value"
                                                stroke="none"
                                            >
                                                {chartData.map((entry, index) => (
                                                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                                                ))}
                                            </Pie>
                                            <RechartsTooltip 
                                                formatter={(value: any) => formatCurrency(Number(value))}
                                                contentStyle={{ borderRadius: '12px', border: 'none', boxShadow: '0 10px 15px -3px rgb(0 0 0 / 0.1)', fontWeight: 'bold' }}
                                            />
                                        </PieChart>
                                    </ResponsiveContainer>
                                </div>
                                
                                <div className="flex flex-wrap justify-center gap-4 md:gap-6 mt-6">
                                    {chartData.map((item, index) => (
                                        <div key={index} className="flex items-center gap-2">
                                            <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: COLORS[index] }}></div>
                                            <span className="text-[11px] font-semibold text-slate-600">{item.name}</span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Amortization Schedule Table */}
            <div className="bg-white rounded-xl border border-slate-200 purple-card-shadow overflow-hidden animate-in slide-in-from-bottom duration-1000">
                <div className="p-6 md:p-8 bg-white text-[#001b4a] flex flex-col md:flex-row justify-between items-center gap-4 border-b border-slate-100 relative">
                    <div className="flex items-center gap-4 relative z-10">
                        <div className="w-12 h-12 rounded-2xl bg-slate-50 flex items-center justify-center text-[#f1ae24] border border-slate-100">
                            <i className="fa-solid fa-list-check text-xl"></i>
                        </div>
                        <div>
                            <h4 className="text-xl font-bold text-[#001b4a]">Amortization Schedule</h4>
                            <p className="text-slate-400 text-xs font-medium mt-1">Yearly Repayment Roadmap</p>
                        </div>
                    </div>
                    <div className="bg-[#f1ae24]/10 px-4 py-2 rounded-lg text-[#f1ae24] text-[11px] font-bold relative z-10">
                        Financial Projection
                    </div>
                </div>

                <div className="overflow-x-auto">
                    <table className="w-full text-left border-collapse">
                        <thead>
                            <tr className="bg-slate-50 border-b border-slate-100">
                                <th className="px-8 py-4 text-[11px] font-bold text-slate-500">Year</th>
                                <th className="px-8 py-4 text-[11px] font-bold text-slate-500">Opening Balance</th>
                                <th className="px-8 py-4 text-[11px] font-bold text-slate-500">Principal Paid</th>
                                <th className="px-8 py-4 text-[11px] font-bold text-slate-500">Interest Paid</th>
                                <th className="px-8 py-4 text-[11px] font-bold text-slate-500">Closing Balance</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100">
                            {amortizationSchedule.map((row) => (
                                <tr key={row.year} className="hover:bg-slate-50 transition-colors group">
                                    <td className="px-8 py-4">
                                        <span className="w-7 h-7 rounded-lg bg-slate-100 flex items-center justify-center font-bold text-xs text-slate-600 group-hover:bg-[#001b4a] group-hover:text-white transition-all">
                                            {row.year}
                                        </span>
                                    </td>
                                    <td className="px-8 py-4 font-semibold text-slate-700 text-sm">{formatCurrency(row.openingBalance)}</td>
                                    <td className="px-8 py-4 font-semibold text-emerald-600 text-sm">+{formatCurrency(row.principalPaid)}</td>
                                    <td className="px-8 py-4 font-semibold text-rose-500 text-sm">-{formatCurrency(row.interestPaid)}</td>
                                    <td className="px-8 py-4 font-bold text-slate-900 text-sm">{formatCurrency(row.closingBalance)}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>

                {/* Footer Insight */}
                <div className="p-6 bg-slate-50/30 border-t border-slate-100">
                    <div className="flex flex-col md:flex-row justify-between items-center gap-4">
                        <p className="text-[11px] font-medium text-slate-400">Calculated based on standard EMI formulas</p>
                        <div className="flex items-center gap-2">
                            <span className="w-2 h-2 rounded-full bg-emerald-500"></span>
                            <span className="text-[11px] font-semibold text-slate-600">All projections are approximate</span>
                        </div>
                    </div>
                </div>
            </div>
            
            <style>{`
                input[type='range']::-webkit-slider-runnable-track {
                    background: transparent;
                }
                input[type='range']::-webkit-slider-thumb {
                    -webkit-appearance: none;
                    appearance: none;
                    width: 18px;
                    height: 18px;
                    background: #f1ae24;
                    border: 3px solid #001b4a;
                    border-radius: 50%;
                    cursor: pointer;
                    margin-top: -8px;
                    box-shadow: 0 4px 6px -1px rgb(0 0 0 / 0.1);
                }
                .recharts-legend-wrapper {
                    display: none !important;
                }
            `}</style>
        </div>
    );
};

export default EMICalculator;
